import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  InputAdornment,
  Tooltip,
  CircularProgress
} from "@material-ui/core";
import { Edit, Delete, Add, EmojiEmotions, Spellcheck, ArrowUpward, ArrowDownward } from "@material-ui/icons";
import { toast } from "react-toastify";
import { Picker } from "emoji-mart";
import "emoji-mart/css/emoji-mart.css";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";

/**
 * ------------------------------------------------------------------
 * MÓDULO DE GESTIÓN DE PROGRAMAS ACADÉMICOS
 * ------------------------------------------------------------------
 * PROHIBIDO MODIFICAR SIN AUTORIZACIÓN EXPLÍCITA
 * 
 * Funcionalidades Críticas:
 * 1. CRUD de Programas (Crear, Editar, Eliminar).
 * 2. Soporte de Emojis (Emoji-Mart) para nombres visuales.
 * 3. Corrección Ortográfica con IA (Gemini).
 * 4. Reordenamiento visual y persistente (Flechas Arriba/Abajo).
 * 
 * @author Trae AI
 * @date 03/03/2026
 * ------------------------------------------------------------------
 */
const AcademicProgramsModal = ({ open, onClose }) => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null); // null = creating
  const [formData, setFormData] = useState({ name: "", status: "active" });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [enhancing, setEnhancing] = useState(false);

  useEffect(() => {
    if (open) {
      fetchPrograms();
      setShowEmoji(false);
    }
  }, [open]);

  const fetchPrograms = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/academic-programs");
      setPrograms(data);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar programas");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return toast.warning("El nombre es obligatorio");

    try {
      if (editingProgram) {
        await api.put(`/academic-programs/${editingProgram.id}`, formData);
        toast.success("Programa actualizado");
      } else {
        await api.post("/academic-programs", formData);
        toast.success("Programa creado");
      }
      fetchPrograms();
      handleCancelEdit();
    } catch (err) {
      toast.error("Error al guardar programa");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/academic-programs/${confirmDeleteId}`);
      toast.success("Programa eliminado");
      fetchPrograms();
      setConfirmDeleteId(null);
    } catch (err) {
      toast.error("Error al eliminar programa");
    }
  };

  const handleEdit = (program) => {
    setEditingProgram(program);
    setFormData({
      name: program.name,
      status: program.status
    });
  };

  const handleCancelEdit = () => {
    setEditingProgram(null);
    setFormData({ name: "", status: "active" });
    setShowEmoji(false);
  };

  const handleEmojiSelect = (emoji) => {
    setFormData({ ...formData, name: formData.name + emoji.native });
    setShowEmoji(false);
  };

  const handleEnhanceText = async () => {
    if (!formData.name.trim()) return;
    setEnhancing(true);
    try {
      const { data } = await api.post("/gemini/enhance", { text: formData.name, type: 'spelling' });
      setFormData({ ...formData, name: data.text });
      toast.success("Ortografía corregida");
    } catch (err) {
      console.error(err);
      toast.error("Error al corregir ortografía");
    } finally {
      setEnhancing(false);
    }
  };

  const handleMove = async (index, direction) => {
    if (loading) return;
    const newPrograms = [...programs];
    const targetIndex = direction === "up" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newPrograms.length) return;

    // Swap items in UI first (Optimistic UI)
    const temp = newPrograms[index];
    newPrograms[index] = newPrograms[targetIndex];
    newPrograms[targetIndex] = temp;
    setPrograms(newPrograms);

    // Prepare payload for backend
    // We send the new orderNumber for ALL items to be safe, 
    // or just the swapped ones. Swapping orderNumbers is efficient.
    const itemA = newPrograms[index];
    const itemB = newPrograms[targetIndex];
    
    // Swap their orderNumbers based on their new visual position
    // Actually, backend reorder expects {id, orderNumber}. 
    // We should map the current visual order to index-based orderNumber.
    const reorderPayload = newPrograms.map((p, idx) => ({
      id: p.id,
      orderNumber: idx + 1 // Start from 1
    }));

    try {
      await api.post("/academic-programs/reorder", { items: reorderPayload });
    } catch (err) {
      console.error(err);
      toast.error("Error al guardar el orden");
      fetchPrograms(); // Revert on error
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Gestión de Programas Académicos</DialogTitle>
        <DialogContent dividers>
          {/* Formulario */}
          <Paper variant="outlined" style={{ padding: 16, marginBottom: 20, backgroundColor: "transparent" }}>
            <Typography variant="subtitle2" gutterBottom>
              {editingProgram ? "Editar Programa" : "Nuevo Programa"}
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" alignItems="center">
                <TextField
                  label="Nombre del Programa"
                  placeholder="Ej: 📚 Bachillerato por Ciclos"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  InputLabelProps={{
                    style: { color: "var(--color-text)" }
                  }}
                  InputProps={{
                    style: { color: "var(--color-text)", backgroundColor: "transparent" },
                    endAdornment: (
                      <InputAdornment position="end">
                         <Tooltip title="Corregir Ortografía (IA)">
                           <IconButton 
                             onClick={handleEnhanceText} 
                             disabled={enhancing || !formData.name} 
                             size="small"
                             edge="end"
                           >
                              {enhancing ? <CircularProgress size={20} /> : <Spellcheck />}
                           </IconButton>
                         </Tooltip>
                      </InputAdornment>
                    )
                  }}
                />
                <IconButton onClick={() => setShowEmoji(!showEmoji)}>
                  <EmojiEmotions color="primary" />
                </IconButton>
              </Box>
              {showEmoji && (
                <Box style={{ position: 'absolute', zIndex: 10, right: 40, top: 160 }}>
                  <Picker 
                    set="apple" 
                    onSelect={handleEmojiSelect} 
                    title="Selecciona un emoji"
                    emoji="point_up"
                    style={{ width: '300px' }}
                  />
                </Box>
              )}
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.status === "active"}
                      onChange={(e) => setFormData({ ...formData, status: e.target.checked ? "active" : "inactive" })}
                      color="primary"
                    />
                  }
                  label={formData.status === "active" ? "Activo" : "Inactivo"}
                />
                <Box>
                  {editingProgram && (
                    <Button onClick={handleCancelEdit} style={{ marginRight: 8 }}>
                      Cancelar
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={editingProgram ? <Edit /> : <Add />}
                    onClick={handleSave}
                  >
                    {editingProgram ? "Actualizar" : "Crear"}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Tabla */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '50px' }}><strong>#</strong></TableCell>
                  <TableCell><strong>Nombre</strong></TableCell>
                  <TableCell align="center"><strong>Estado</strong></TableCell>
                  <TableCell align="center"><strong>Orden</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {programs.map((program, index) => (
                  <TableRow key={program.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontSize: '1rem' }}>
                        {program.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: 4,
                          fontSize: "0.75rem",
                          backgroundColor: program.status === "active" ? "#e8f5e9" : "#ffebee",
                          color: program.status === "active" ? "#2e7d32" : "#c62828"
                        }}
                      >
                        {program.status === "active" ? "ACTIVO" : "INACTIVO"}
                      </span>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleMove(index, "up")} disabled={index === 0}>
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleMove(index, "down")} disabled={index === programs.length - 1}>
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleEdit(program)} color="primary">
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => setConfirmDeleteId(program.id)} style={{ color: "#d32f2f" }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {programs.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No hay programas registrados.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Eliminar Programa"
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleDelete}
      >
        ¿Estás seguro de que deseas eliminar este programa?
      </ConfirmationModal>
    </>
  );
};

export default AcademicProgramsModal;
